import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { YocoCheckoutButton } from '@/components/YocoCheckoutButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ListProperty = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [discountCode, setDiscountCode] = useState('');
  const [validatedCode, setValidatedCode] = useState(null);
  const [validating, setValidating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [hasPayment, setHasPayment] = useState(false);

  useEffect(() => {
    if (user?.role === 'host') {
      checkExistingPayment();
    }
  }, [user]);

  const checkExistingPayment = async () => {
    try {
      const { data } = await axios.get(`${API}/host/dashboard`, { withCredentials: true });
      // If host has active listings, they have payment
    } catch (e) {
      // No existing dashboard endpoint, this is fine
    }
  };

  const handleValidateCode = async () => {
    if (!discountCode.trim()) return;
    setValidating(true);
    try {
      const { data } = await axios.post(
        `${API}/discount-codes/validate`,
        { code: discountCode },
        { withCredentials: true }
      );
      setValidatedCode(data);
      toast.success(`Code applied! ${data.discount_value}% off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
      setValidatedCode(null);
    } finally {
      setValidating(false);
    }
  };

  const handleFreeListing = async () => {
    setProcessing(true);
    try {
      await axios.post(
        `${API}/discount-codes/apply-free`,
        { code: validatedCode.code },
        { withCredentials: true }
      );
      toast.success('Free listing activated! Redirecting...');
      setTimeout(() => navigate('/host/create-listing'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply code');
    } finally {
      setProcessing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center py-16 px-6">
        <div className="max-w-md text-center">
          <div className="label-caps text-gray-600 mb-3">Get Started</div>
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            List your <em className="italic">property</em>
          </h1>
          <p className="text-gray-700 mb-8">
            Create a host account to start listing your furnished monthly rental.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/register')}
              className="bg-black hover:bg-gray-800 text-white rounded-none label-caps px-8 py-6"
              data-testid="list-property-register-btn"
            >
              Create Host Account
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="border-black text-black hover:bg-black hover:text-white rounded-none label-caps px-8 py-6 bg-transparent"
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (user.role !== 'host') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center py-16 px-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-4xl mb-4">Host account needed</h1>
          <p className="text-gray-700 mb-8">You&apos;ll need a host account to list properties. Please register a new host account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-16">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="mb-12 pb-8 border-b border-gray-300">
          <div className="label-caps text-gray-600 mb-3">Step 1 of 3</div>
          <h1 className="font-serif text-4xl md:text-5xl mb-3" data-testid="list-property-title">
            List your <em className="italic">property</em>
          </h1>
          <p className="text-gray-700 max-w-xl">
            Pay the R950 listing fee to activate your host account. Valid for three months, unlimited enquiries.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="border border-gray-400 p-8 md:p-12 mb-8 bg-cream" data-testid="pricing-card">
          <div className="flex justify-between items-start mb-8 pb-6 border-b border-gray-300">
            <div>
              <div className="label-caps text-gray-600 mb-2">Listing Fee</div>
              <div className="font-serif text-5xl md:text-6xl">
                {validatedCode?.discount_value === 100 ? (
                  <><span className="line-through text-gray-400">R950</span> <span className="text-green-700">R0</span></>
                ) : (
                  'R950'
                )}
              </div>
              <div className="text-sm text-gray-600 mt-2 font-serif italic">Valid for 3 months</div>
            </div>
          </div>

          <ul className="space-y-3 mb-8">
            {[
              'Unlimited enquiries',
              'Zero booking commission',
              'Direct guest communication',
              'Verified badge (after approval)',
              'Photo & video uploads',
              'Calendar management',
              'Renew anytime'
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-gray-800">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* Discount Code Section */}
          <div className="border-t border-gray-300 pt-6 mb-6">
            <Label htmlFor="discount-code" className="label-caps text-gray-700 mb-2 block">
              Have a code?
            </Label>
            {validatedCode ? (
              <div className="flex items-center justify-between bg-black text-white p-4">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                  <div>
                    <div className="font-serif text-lg">{validatedCode.code}</div>
                    <div className="text-xs text-gray-300">
                      {validatedCode.description}
                      {validatedCode.uses_remaining !== null && ` · ${validatedCode.uses_remaining} spots left`}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { setValidatedCode(null); setDiscountCode(''); }}
                  className="text-sm hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  id="discount-code"
                  type="text"
                  placeholder="Enter code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  className="rounded-none border-gray-400 bg-cream focus-visible:ring-black flex-1"
                  data-testid="discount-code-input"
                />
                <Button
                  onClick={handleValidateCode}
                  disabled={validating || !discountCode.trim()}
                  variant="outline"
                  className="border-black text-black hover:bg-black hover:text-white rounded-none label-caps bg-transparent"
                  data-testid="discount-code-apply-btn"
                >
                  {validating ? 'Checking...' : 'Apply'}
                </Button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2 font-serif italic">
              Founding members can list free — first 50 hosts only
            </p>
          </div>

          {/* Payment Button */}
          {validatedCode?.discount_value === 100 ? (
            <button
              onClick={handleFreeListing}
              disabled={processing}
              className="btn btn-sea btn-lg w-full disabled:opacity-60"
              data-testid="activate-free-listing-btn"
            >
              {processing ? 'Activating...' : 'Activate Free Listing'} →
            </button>
          ) : (
            <YocoCheckoutButton
              amountZar={950}
              customerName={user.name}
              customerEmail={user.email}
              description="Cape Town Monthly — 3 Month Listing Fee"
              metadata={{ type: 'listing' }}
              label="Pay R950 with Yoco"
              className="w-full justify-center py-6 text-[12px]"
              testId="pay-with-yoco-btn"
            />
          )}

          <p className="text-xs text-gray-500 text-center mt-4 font-serif italic">
            After payment, you&apos;ll be able to create your listing with photos, video, and verification
          </p>
        </div>

        {/* Info */}
        <div className="bg-cream-darker p-6 border border-gray-300">
          <div className="label-caps text-gray-700 mb-3">What happens next?</div>
          <ol className="space-y-2 text-sm text-gray-800">
            <li className="flex items-baseline gap-3">
              <span className="font-serif italic text-gray-500">i.</span>
              <span>Complete payment or activate free listing</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="font-serif italic text-gray-500">ii.</span>
              <span>Fill in your property details, upload 20 photos & videos</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="font-serif italic text-gray-500">iii.</span>
              <span>Submit a verification walkthrough video</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="font-serif italic text-gray-500">iv.</span>
              <span>We review within 24-48 hours, then your listing goes live</span>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};
